import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { isProductVisibleInContext } from '../../services/productService';
import { getCategoryLabel } from '../../services/categoryService';
import Markdown from '../Markdown';
import './ProductCard.scss';

const ProductCard = ({ product, context = 'catalog', showImage = true, showDescription = true, showPrice = false, showTags = false, showFeatures = false, onClick }) => {
  const navigate = useNavigate();

  // Check if product should be visible in this context
  if (!isProductVisibleInContext(product, context)) {
    return null;
  }

  const handleClick = (e) => {
    if (onClick) {
      e.preventDefault();
      onClick(product);
    }
  };

  return (
    <motion.div
      className={`product-card card ${product.visibility.featured ? 'featured' : ''} ${product.visibility.new ? 'new' : ''}`}
      whileHover={{ y: -10 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Link to={`/products/${product.id}`} className="product-link" onClick={handleClick}>
        {/* Product Badges */}
        <div className="product-badges">
          {product.visibility.featured && (
            <span className="badge badge-featured">Featured</span>
          )}
          {product.visibility.new && (
            <span className="badge badge-new">New</span>
          )}
          {product.visibility.onSale && (
            <span className="badge badge-sale">Sale</span>
          )}
          {product.type === 'showcase' && (
            <span className="badge badge-showcase">Showcase</span>
          )}
        </div>

        {/* Product Image */}
        {showImage && product.images && product.images.length > 0 && (
          <div className="product-image-container">
            <img
              src={product.images[0]}
              alt={product.name}
              className="product-image"
              loading="lazy"
            />
            {product.images.length > 1 && (
              <div className="image-count">+{product.images.length - 1}</div>
            )}
          </div>
        )}

        {/* Product Content */}
        <div className="product-content">
          {/* Category */}
          <div className="product-category">
            {getCategoryLabel(product.category)}
          </div>

          {/* Title */}
          <h3 className="product-title">{product.name}</h3>

          {/* Short Description */}
          {showDescription && product.shortDescription && (
            <Markdown className="product-short-description">{product.shortDescription}</Markdown>
          )}

          {/* Features */}
          {showFeatures && product.features && product.features.length > 0 && (
            <ul className="product-features-preview">
              {product.features.slice(0, 3).map((feature, index) => (
                <li key={index} className="feature-item">
                  <span className="feature-icon">✓</span>
                  <Markdown inline>{feature}</Markdown>
                </li>
              ))}
              {product.features.length > 3 && (
                <li className="feature-more">+{product.features.length - 3} more</li>
              )}
            </ul>
          )}

          {/* Tags */}
          {showTags && product.tags && product.tags.length > 0 && (
            <div className="product-tags">
              {product.tags.slice(0, 3).map((tag, index) => (
                <span key={index} className="tag">{tag}</span>
              ))}
              {product.tags.length > 3 && (
                <span className="tag-more">...</span>
              )}
            </div>
          )}

          {/* Price (if enabled) */}
          {showPrice && product.visibility.showPrice && product.pricing.enabled && (
            <div className="product-price">
              {product.pricing.priceType === 'quote' ? (
                <span className="price-quote">Contact for Quote</span>
              ) : (
                <>
                  <span className="price-amount">
                    {product.pricing.currency} {product.pricing.price.toLocaleString()}
                  </span>
                  {product.pricing.priceType === 'monthly' && (
                    <span className="price-period">/month</span>
                  )}
                  {product.pricing.priceType === 'annual' && (
                    <span className="price-period">/year</span>
                  )}
                </>
              )}
            </div>
          )}

          {/* Showcase Details (if applicable) */}
          {product.type === 'showcase' && product.showcaseDetails && (
            <div className="showcase-preview">
              <div className="showcase-client">
                <strong>Client:</strong> <Markdown inline>{product.showcaseDetails.client}</Markdown>
              </div>
              <div className="showcase-results">
                <strong>Results:</strong> <Markdown inline>{product.showcaseDetails.results}</Markdown>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="product-actions">
            {product.visibility.showDemoLink && product.featureFlags.hasDemo && (
              <button className="btn btn-outline btn-sm" onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (product.demoUrl) {
                  window.open(product.demoUrl, '_blank', 'noopener,noreferrer');
                } else {
                  alert(`Demo not configured yet for: ${product.name}.`);
                }
              }}>
                View Demo
              </button>
            )}

            {product.visibility.showContactForm && (
              <button className="btn btn-sm" onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate('/contact', { state: { productId: product.id, productName: product.name } });
              }}>
                Contact Us
              </button>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;